/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, batch, onConnect, withAssign } from '@reatom/framework'

import { pipe, Result } from '@blade/std-lib'
import { isLoggedInAtom } from '@root/entities/auth'
import { selectedNetworkAtom } from '@root/entities/network'
import { fetchTradesRequestAction } from '@root/entities/trade'
import { MakerAddress } from '@root/entities/trade/model/maker-address'
import { walletAddressesAtom } from '@root/entities/wallet'
import { setMakerFilterEvent } from '@root/features/trade/tables/trades-table/atoms'
import { dataTableNoAccessError, reatomDataTable } from '@root/shared/ui-kit'

import { createTradeTableColumns } from '../ui/trade-table-columns'

import { reatomTradesTableMakers } from './reatom-trades-table-makers'
import { reatomTradesTableRealtime } from './reatom-trades-table-realtime'

import type { Ctx } from '@reatom/framework'
import type { Address } from '@root/entities/address/Address'
import type { PairAddress, PairModel } from '@root/entities/pair'
import type { Trade } from '@root/entities/trade'
import type { TableData } from '@root/shared/ui-kit'
import type { TableFilters } from '@root/shared/ui-kit/data-table/atoms/table-filter'

export type TradeTableColumnId =
  | 'timestamp'
  | 'price'
  | 'type'
  | 'amount'
  | 'mcap'
  | 'total-base'
  | 'total-usd'
  | 'maker'
  | 'quote-currency'

export const tradesTableMakerFilterAtom = atom<Address | undefined>(
  undefined,
  'tradesTableMakerFilterAtom',
)
export { tradesPausedAtom } from './reatom-trades-table-realtime'

export const reatomTradesTable = ({
  pairAddress,
  pair,
  initialMakerFilter,
  wallets,
}: {
  pairAddress: PairAddress | undefined
  pair: PairModel | undefined
  wallets?: 'user-wallets' | Array<Address>
  initialMakerFilter?: string | ((ctx: Ctx) => string | undefined)
}) => {
  const makersCacheModel = reatomTradesTableMakers()

  const walletsAtom = atom((ctx) => {
    if (wallets === 'user-wallets') {
      return [...ctx.spy(walletAddressesAtom)]
    }
    return wallets
  }, 'walletsAtom')

  const paramsAtom = atom((ctx) => {
    const network = ctx.spy(selectedNetworkAtom)
    const walletAddresses = ctx.spy(walletsAtom)
    return { walletAddresses, network }
  }, 'paramsAtom')

  const tableModel = reatomDataTable({
    columns: createTradeTableColumns({
      withMakerFilter: !wallets,
      withCurrency: !pairAddress,
      aliasUserWallet: !wallets,
      showOwner: !wallets,
      pair,
    }),
    paramsAtom,
    name: 'trades',
    // Every trade that passes the filter used to be prepended and kept. On a
    // busy pair that is thousands a minute, each one carrying its maker and
    // its token metadata, and the array was copied whole on every animation
    // frame. Five hundred rows is more than the virtualised list can show and
    // more than anyone scrolls back through.
    maxItems: 500,
    initialFilters: (ctx) => {
      const makerFilter = ((): TableFilters<TradeTableColumnId> => {
        if (wallets) {
          return {}
        }
        if (initialMakerFilter) {
          const makerFilter =
            typeof initialMakerFilter === 'function'
              ? initialMakerFilter(ctx)
              : initialMakerFilter

          if (!makerFilter) {
            return {}
          }

          return {
            maker: {
              column: 'maker',
              type: 'string',
              operator: 'eq',
              value: makerFilter,
            },
          }
        }
        return {}
      })()

      const defaultFilters: TableFilters<TradeTableColumnId> = wallets
        ? {}
        : {
            'total-usd': {
              column: 'total-usd',
              type: 'numeric',
              operator: 'gte',
              value: 1,
            },
          }

      return {
        ...defaultFilters,
        ...makerFilter,
      }
    },
    loadItems: async (
      ctx,
      {
        sorting,
        pagination: { cursor, limit },
        filters,
        params: { walletAddresses, network },
      },
    ) => {
      const getMakerFilter = () => {
        if (filters.maker?.type === 'string') {
          return filters.maker.value
        }

        return walletAddresses
      }

      if (walletAddresses && walletAddresses.length === 0) {
        return Result.succeed({ data: [], cursor: undefined })
      }

      if (wallets === 'user-wallets') {
        const isLoggedIn = ctx.get(isLoggedInAtom)
        if (!isLoggedIn) {
          return Result.fail(dataTableNoAccessError())
        }
      }

      const maker = getMakerFilter()
      const minVolumeBase =
        filters['total-base']?.type === 'numeric'
          ? filters['total-base'].value
          : undefined
      const minVolumeUsd =
        filters['total-usd']?.type === 'numeric'
          ? filters['total-usd'].value
          : undefined

      const result = await fetchTradesRequestAction(ctx, {
        limit,
        cursor,
        network,
        poolAddress: pairAddress,
        sort: sorting?.sortDirection ?? 'desc',
        maker,
        minVolumeUsd: minVolumeUsd?.toString(),
        minVolumeBase: minVolumeBase?.toString(),
        tokenMeta: !pairAddress,
      })

      if (Result.isSuccess(result)) {
        setPaused(ctx, false)

        for (const trade of result.success.data) {
          makersCacheModel.storeMaker(ctx, trade)
          if (trade.type === 'liquidityRemove' && pair) {
            pair.hasLiquidityRemovedTx(ctx, true)
          }
        }
      }

      return pipe(
        result,
        Result.map(
          (data): TableData<Trade.Trade> => ({
            data: data.data,
            cursor: data.nextCursor,
            hasNext: data.hasNext,
          }),
        ),
      )
    },
    limit: 20,
    initialSorting: {
      sortColumn: 'timestamp',
      sortDirection: 'desc',
    },
    getId: (item) => item.id,
  })
  const {
    paused,
    setPaused,
    buffedCount,
    initialize: initializeRealtime,
  } = reatomTradesTableRealtime({
    pairAddress,
    wallets: walletsAtom,
    tableModel,
  })

  const setMakerFilterAction = action((ctx, maker: string | undefined) => {
    if (!maker) {
      tableModel.setColumnFilter(ctx, 'maker', undefined)
      return
    }
    tableModel.setColumnFilter(ctx, 'maker', {
      type: 'string',
      column: 'maker',
      operator: 'eq',
      value: maker,
    })
  }, 'setMakerFilterAction')

  onConnect(tableModel, (ctx) => {
    const unsubscribeMakerFilter = setMakerFilterEvent.onCall(
      (ctx, { maker }) => {
        setMakerFilterAction(ctx, maker)
      },
    )

    return () => {
      setPaused(ctx, false)
      unsubscribeMakerFilter()
    }
  })

  const makerFilterAtom = atom((ctx) => {
    const filters = ctx.spy(tableModel.filters)
    const filterValue =
      filters.maker?.type === 'string'
        ? MakerAddress(filters.maker.value)
        : undefined
    return filterValue
  }, 'makerFilterAtom')

  const initializeAction = action((ctx) => {
    const unsubStateChange = tableModel.onChange((ctx, state) => {
      const items = state.value?.data ?? []
      batch(ctx, () => {
        for (const item of items) {
          makersCacheModel.storeMaker(ctx, item)
        }
      })
    })

    const realtimeUnsub = initializeRealtime(ctx)
    return () => {
      unsubStateChange()
      realtimeUnsub()
    }
  }, 'initializeAction')

  return tableModel.pipe(
    withAssign(() => ({
      paused,
      setPaused,
      buffedCount,
      makerFilter: makerFilterAtom,
      setMakerFilter: setMakerFilterAction,
      initialize: initializeAction,
      makersCache: makersCacheModel.makersCache,
    })),
  )
}

export type TradesTableModel = ReturnType<typeof reatomTradesTable>
export type TradesTableModelColumns = TradesTableModel['columns']
